import { useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/config";

interface CreateUserDialogProps {
    initialEmail?: string;
    onClose: () => void;
    onCreated: (uid: string) => void;
    onViewUser: (uid: string) => void;
}

interface CheckEmailResult {
    exists: boolean;
    checkedEmail?: string;
    uid?: string;
    name?: string | null;
    surname?: string | null;
}

interface CreateUserResult {
    success: boolean;
    uid: string;
    message: string;
}

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({ initialEmail, onClose, onCreated, onViewUser }) => {
    const [step, setStep] = useState<"email" | "details">("email");
    const [email, setEmail] = useState(initialEmail ?? "");
    const [name, setName] = useState("");
    const [surname, setSurname] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [existing, setExisting] = useState<CheckEmailResult | null>(null);
    const [result, setResult] = useState<CreateUserResult | null>(null);

    const handleCheckEmail = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        setExisting(null);

        try {
            const checkEmail = httpsCallable<{ email: string }, CheckEmailResult>(functions, "checkUserEmailAsAdmin");
            const { data } = await checkEmail({ email: email.trim() });
            if (data.exists) {
                setExisting({ ...data, checkedEmail: email.trim().toLowerCase() });
            } else {
                setStep("details");
            }
        } catch (err: any) {
            console.error("[CreateUserDialog] Email check failed:", err);
            setError(err?.message ?? "Failed to check email. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(null);

        try {
            const createUser = httpsCallable<
                { email: string; name?: string; surname?: string },
                CreateUserResult
            >(functions, "createUserAsAdmin");

            const { data } = await createUser({
                email: email.trim(),
                ...(name.trim() && { name: name.trim() }),
                ...(surname.trim() && { surname: surname.trim() }),
            });
            setResult(data);
            onCreated(data.uid);
        } catch (err: any) {
            console.error("[CreateUserDialog] Failed:", err);
            setError(err?.message ?? "Failed to create user. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const changeEmail = () => {
        setStep("email");
        setExisting(null);
        setError(null);
    };

    // The warning only applies to the email that was checked; editing it clears the warning.
    const existingMatch = existing && existing.checkedEmail === email.trim().toLowerCase() ? existing : null;
    const existingName = [existingMatch?.name, existingMatch?.surname].filter(Boolean).join(" ");

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <h2 className="font-semibold text-gray-900">Create User</h2>
                    <button onClick={onClose} className="btn btn-ghost btn-xs btn-square">
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>

                {step === "email" ? (
                    <form onSubmit={handleCheckEmail} className="px-6 py-5 flex flex-col gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Email <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="user@example.com"
                                className="input input-bordered w-full"
                                required
                                autoFocus
                            />
                        </div>

                        {error && <p className="text-sm text-red-500">{error}</p>}
                        {existingMatch && (
                            <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 flex flex-col gap-2">
                                <div>
                                    A user with this email already exists
                                    {existingName ? ` (${existingName})` : ""}.
                                    <div className="text-xs mt-1 font-mono break-all">UID: {existingMatch.uid}</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => existingMatch.uid && onViewUser(existingMatch.uid)}
                                    className="btn btn-sm btn-outline self-start"
                                >
                                    View user
                                </button>
                            </div>
                        )}

                        <div className="flex items-center justify-end gap-3 pt-1">
                            <button type="button" onClick={onClose} className="btn btn-ghost">
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={busy || !email.trim() || !!existingMatch}
                                className="btn bg-gray-900 text-white hover:bg-gray-700 border-none gap-2 disabled:opacity-50"
                            >
                                {busy && <span className="loading loading-spinner loading-xs" />}
                                {busy ? "Checking…" : "Continue"}
                            </button>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleCreate} className="px-6 py-5 flex flex-col gap-4">
                        <div className="flex items-center justify-between text-sm bg-gray-50 border rounded-lg px-3 py-2">
                            <span className="truncate">{email.trim()}</span>
                            {!result && (
                                <button type="button" onClick={changeEmail} className="text-xs text-primary hover:underline shrink-0 ml-3">
                                    Change
                                </button>
                            )}
                        </div>
                        <p className="text-xs text-gray-500">No existing user with this email. Add their details to create the account. No email will be sent to them.</p>

                        <div className="flex gap-3">
                            <div className="flex flex-col gap-1.5 flex-1">
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Jane"
                                    className="input input-bordered w-full"
                                    disabled={!!result}
                                    autoFocus
                                />
                            </div>
                            <div className="flex flex-col gap-1.5 flex-1">
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    value={surname}
                                    onChange={(e) => setSurname(e.target.value)}
                                    placeholder="Smith"
                                    className="input input-bordered w-full"
                                    disabled={!!result}
                                />
                            </div>
                        </div>

                        {error && <p className="text-sm text-red-500">{error}</p>}
                        {result && <p className="text-sm text-green-600">{result.message}</p>}

                        <div className="flex items-center justify-end gap-3 pt-1">
                            <button type="button" onClick={onClose} className="btn btn-ghost">
                                {result ? "Close" : "Cancel"}
                            </button>
                            {result && (
                                <button
                                    type="button"
                                    onClick={() => onViewUser(result.uid)}
                                    className="btn bg-gray-900 text-white hover:bg-gray-700 border-none"
                                >
                                    View user
                                </button>
                            )}
                            {!result && (
                                <button
                                    type="submit"
                                    disabled={busy}
                                    className="btn bg-gray-900 text-white hover:bg-gray-700 border-none gap-2 disabled:opacity-50"
                                >
                                    {busy && <span className="loading loading-spinner loading-xs" />}
                                    {busy ? "Creating…" : "Create User"}
                                </button>
                            )}
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default CreateUserDialog;
